import { useState, useEffect, useMemo } from "react";
import {
  getRunTimeHours,
  ItemOperation,
  OperationQuantityBreak,
  RunTimeFormat,
  OPERATION_SOURCE_TYPE,
  UNIT_COST_CALCULATION,
} from "@forgepoint/shared";
import { useOperationsStore } from "@/modules/operations/operationsStore";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import BOMOperationSheet from "./BOMOperationSheet";
import Decimal from "decimal.js";
import { Lock } from "lucide-react";
import { formatRunTime } from "./utils";

interface BOMOperationRowProps {
  itemOperationId?: string;
  operationId: string;
  rowKey: string;
  sequenceNumber?: string | null;
  setupTime?: string;
  runTimePerItem?: string;
  qtyPer?: string;
  runTimeFormat?: RunTimeFormat | null;
  efficiency?: string;
  overrideOperationCost?: string;
  overrideSetupRate?: string;
  minimumCharge?: string;
  quantityBreaks?: OperationQuantityBreak[];
  quantities: string[];
  reasoning?: string;
  depth?: number;
  onSave?: (operation: ItemOperation) => Promise<boolean> | boolean;
  onRemove?: () => void;
  disabled?: boolean;
  disabledReason?: string;
  onUpdated?: () => void;
  showSequenceNumber: boolean;
}

export default function BOMOperationRow({
  itemOperationId,
  operationId,
  rowKey,
  sequenceNumber,
  setupTime,
  runTimePerItem,
  qtyPer,
  runTimeFormat,
  efficiency,
  overrideOperationCost,
  overrideSetupRate,
  minimumCharge,
  quantityBreaks,
  quantities,
  reasoning,
  depth = 0,
  onSave,
  onRemove,
  disabled = false,
  disabledReason,
  onUpdated,
  showSequenceNumber,
}: BOMOperationRowProps) {
  const { operations, fetchOperation } = useOperationsStore();
  const [isLoadingOperation, setIsLoadingOperation] = useState(false);
  const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);

  const operation = operations.get(operationId);

  useEffect(() => {
    const loadOperation = async () => {
      if (!operation && operationId) {
        setIsLoadingOperation(true);
        try {
          await fetchOperation(operationId);
        } finally {
          setIsLoadingOperation(false);
        }
      }
    };
    loadOperation();
  }, [operationId, operation, fetchOperation]);

  const effectiveSetupRate = overrideSetupRate || operation?.setupRate;

  const operationCosts = useMemo(() => {
    if (!operation) {
      return quantities.map(() => ({ total: 0, unit: 0 }));
    }

    const isPerUnitCostCalculation =
      operation.unitCostCalculation === UNIT_COST_CALCULATION.PER_UNIT;

    const defaultCost = isPerUnitCostCalculation
      ? operation.cost
      : new Decimal(operation.cost).plus(operation.burdenRate ?? 0);
    const baseCost = overrideOperationCost || defaultCost.toString();

    const getCostForQuantity = (qty: Decimal): string => {
      if (!quantityBreaks || quantityBreaks.length === 0) {
        return baseCost;
      }
      const sorted = [...quantityBreaks].sort((a, b) => b.minQty - a.minQty);
      const applicable = sorted.find((qb) => qty.gte(qb.minQty));
      if (!applicable) {
        return baseCost;
      }
      return applicable.overrideOperationCost || baseCost;
    };

    if (isPerUnitCostCalculation) {
      return quantities.map((qty) => {
        const quantity = qty ? new Decimal(qty) : new Decimal(0);
        const costToUse = getCostForQuantity(quantity);
        const unitCostValue = new Decimal(costToUse || 0).times(qtyPer ?? "1");
        const standardCost = unitCostValue.times(quantity);
        const totalCost = minimumCharge
          ? Decimal.max(standardCost, new Decimal(minimumCharge))
          : standardCost;
        return {
          total: totalCost.toNumber(),
          unit: totalCost
            .dividedBy(quantity.equals(0) ? new Decimal(1) : quantity)
            .toNumber(),
        };
      });
    }

    return quantities.map((qty) => {
      const setup = new Decimal(setupTime || 0);
      const runtime = new Decimal(
        getRunTimeHours(runTimePerItem || "0", runTimeFormat, qtyPer ?? "1") ??
          0,
      );
      const quantity = qty ? new Decimal(qty) : new Decimal(0);
      const costToUse = getCostForQuantity(quantity);
      const runHourlyRate = new Decimal(costToUse || 0);
      const setupHourlyRate = effectiveSetupRate
        ? new Decimal(effectiveSetupRate)
        : runHourlyRate;
      const efficiencyValue = efficiency ? new Decimal(efficiency) : null;
      const hasValidEfficiency =
        efficiencyValue !== null && efficiencyValue.greaterThan(0);

      if (!hasValidEfficiency || quantity.equals(0)) {
        return { total: 0, unit: 0 };
      }

      const efficiencyFactor = new Decimal(100).dividedBy(efficiencyValue);

      const setupCost = setup.times(efficiencyFactor).times(setupHourlyRate);
      const runCost = runtime
        .times(quantity)
        .times(efficiencyFactor)
        .times(runHourlyRate);
      const totalCost = setupCost.plus(runCost);
      const unitCost = totalCost.dividedBy(quantity);

      return { total: totalCost.toNumber(), unit: unitCost.toNumber() };
    });
  }, [
    operation,
    setupTime,
    runTimePerItem,
    runTimeFormat,
    efficiency,
    overrideOperationCost,
    effectiveSetupRate,
    minimumCharge,
    quantityBreaks,
    quantities,
    qtyPer,
  ]);

  // Add 16px base indent for operations on mobile, 24px on desktop, plus additional indent based on depth
  const indentStyle =
    depth > 0 ? { paddingLeft: `${Math.min(depth * 16, 48)}px` } : {};

  const currentOperation: ItemOperation = {
    id: itemOperationId,
    operationId,
    sequenceNumber,
    setupTime,
    runTimePerItem,
    qtyPer,
    runTimeFormat,
    efficiency: efficiency ?? "100",
    overrideOperationCost,
    overrideSetupRate,
    minimumCharge,
    quantityBreaks,
    reasoning: reasoning || "",
    description: operation?.name,
  };

  const handleSave = async (
    updatedOperation: ItemOperation,
  ): Promise<boolean> => {
    if (!onSave) {
      return true;
    }
    // A failed save keeps the sheet open instead of closing on stale state.
    return await onSave(updatedOperation);
  };

  const operationDetails = (
    <>
      {operation?.unitCostCalculation === UNIT_COST_CALCULATION.PER_UNIT ? (
        <>
          <span>
            Unit cost{" "}
            <span className="text-foreground tabular-nums">
              ${overrideOperationCost || operation.cost}
            </span>
            {overrideOperationCost && " (overridden)"}
          </span>
          {minimumCharge && (
            <span>
              Min. charge{" "}
              <span className="text-foreground tabular-nums">
                ${minimumCharge}
              </span>
            </span>
          )}
          {(setupTime || runTimePerItem) && (
            <>
              <span>
                Setup{" "}
                <span className="text-foreground tabular-nums">
                  {setupTime || "0"}h
                </span>
              </span>
              <span>
                Runtime{" "}
                <span className="text-foreground tabular-nums">
                  {formatRunTime(runTimePerItem, qtyPer, runTimeFormat)}
                </span>
              </span>
            </>
          )}
        </>
      ) : (
        <>
          <span>
            Rate{" "}
            <span className="text-foreground tabular-nums">
              $
              {overrideOperationCost ||
                new Decimal(operation?.cost ?? 0)
                  .plus(operation?.burdenRate ?? 0)
                  .toString()}
              /hr
            </span>
            {overrideOperationCost && " (overridden)"}
          </span>
          {effectiveSetupRate && (
            <span>
              Setup rate{" "}
              <span className="text-foreground tabular-nums">
                ${effectiveSetupRate}/hr
              </span>
              {overrideSetupRate && " (overridden)"}
            </span>
          )}
          <span>
            Setup{" "}
            <span className="text-foreground tabular-nums">
              {setupTime || "0"}h
            </span>
          </span>
          <span>
            Runtime{" "}
            <span className="text-foreground tabular-nums">
              {formatRunTime(runTimePerItem, qtyPer, runTimeFormat)}
            </span>
          </span>
          {efficiency && (
            <span>
              Efficiency{" "}
              <span className="text-foreground tabular-nums">
                {efficiency}%
              </span>
            </span>
          )}
        </>
      )}
      {qtyPer && qtyPer !== "1" && (
        <span>
          Qty/parent{" "}
          <span className="text-foreground tabular-nums">{qtyPer}</span>
        </span>
      )}
    </>
  );

  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <tr
              className="transition-colors touch-manipulation hover:bg-accent/30 cursor-pointer"
              onClick={() => setIsEditSheetOpen(true)}
            >
              <td
                className="py-2 px-2 sm:px-4 w-[300px] min-w-[300px] sticky left-0 z-10 bg-card border-r border-border border-b border-b-border"
                data-field-anchor={`operations.${rowKey}`}
              >
                <div className="min-w-0 space-y-1.5" style={indentStyle}>
                  <div className="flex h-5 items-start gap-2 text-xs sm:text-sm font-medium">
                    {showSequenceNumber && sequenceNumber && (
                      <span className="shrink-0 max-w-[12ch] break-all tabular-nums text-muted-foreground">
                        {sequenceNumber}
                      </span>
                    )}
                    <span className="flex min-w-0 items-baseline gap-1">
                      <span className="max-w-full shrink-0 truncate">
                        {isLoadingOperation
                          ? "Loading..."
                          : operation?.name || "Unknown Operation"}
                      </span>
                      {operation?.description &&
                        operation.description !== operation.name && (
                          <span
                            className="truncate font-normal"
                            title={operation.description}
                          >
                            {" — "}
                            {operation.description}
                          </span>
                        )}
                    </span>
                  </div>
                  {operation && (
                    <div className="flex h-5 items-center gap-1">
                      <Badge
                        variant="outline"
                        className="text-[10px] sm:text-xs font-normal whitespace-nowrap"
                      >
                        {operation.operationSourceType ===
                        OPERATION_SOURCE_TYPE.OUTSIDE
                          ? "Outside"
                          : "Inside"}
                      </Badge>
                      <Badge
                        variant="outline"
                        className="text-[10px] sm:text-xs font-normal whitespace-nowrap"
                      >
                        {operation.unitCostCalculation ===
                        UNIT_COST_CALCULATION.PER_UNIT
                          ? "Per Unit"
                          : "Hourly"}
                      </Badge>
                    </div>
                  )}
                  <div className="line-clamp-2 h-8 text-[10px] sm:text-xs leading-4 text-muted-foreground [&_span]:leading-4 [&>span]:mr-3 [&>span]:inline-block">
                    {operationDetails}
                  </div>
                  <p
                    className="line-clamp-1 h-4 text-[10px] sm:text-xs leading-4 text-muted-foreground"
                    title={reasoning}
                  >
                    {reasoning || "\u00a0"}
                  </p>
                </div>
              </td>
              {operationCosts.map((cost, i) => (
                <td
                  key={i}
                  className="py-2 px-2 sm:px-4 text-right text-xs sm:text-sm border-l border-border border-b border-b-border tabular-nums"
                >
                  <div>
                    $
                    {cost.unit.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 3,
                    })}
                    /ea
                  </div>
                  <div className="text-[10px] text-muted-foreground">
                    $
                    {cost.total.toLocaleString("en-US", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 3,
                    })}
                  </div>
                </td>
              ))}
            </tr>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-sm">
            {operation?.description && (
              <p className="mb-1 font-medium">
                {operation.name} — {operation.description}
              </p>
            )}
            <div className="mb-2 flex flex-wrap gap-x-3 gap-y-1 text-xs [&_span]:text-inherit">
              {operationDetails}
            </div>
            {disabled ? (
              <p className="flex items-center gap-1.5">
                <Lock className="h-3 w-3" />
                {disabledReason ?? "This row is read-only"}. Click to open the
                operation.
              </p>
            ) : (
              <p>Click to edit</p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <BOMOperationSheet
        open={isEditSheetOpen}
        onOpenChange={setIsEditSheetOpen}
        mode="edit"
        operation={currentOperation}
        onSave={handleSave}
        onRemove={onRemove}
        onUpdated={onUpdated}
        locked={disabled}
        lockReason={disabledReason}
      />
    </>
  );
}
